import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import { CircularProgress } from '@mui/material';
import styled, { css } from 'styled-components';
import { useLocation } from 'react-router-dom';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { useQueryClient } from '@tanstack/react-query';
import useAiQuota from '../../../hooks/useAiQuota';
import usePrevious from '../../../hooks/usePrevious';
import { CHAT_HISTORY } from '../../../constants/queryKeys';
import { Flex, FlexColumn } from '../../Flex';
import IconButton from '../../IconButton';
import TextField from '../../TextField';
import {
  ChatMessage as ChatMessageType,
  ChatMode,
  ModelProvider,
  QuotaScope,
  Suggestion,
  ToolRendererFunction,
} from '../types';
import useChatStream from '../hooks/useChatStream';
import useSessionStream from '../hooks/useSessionStream';
import useAccount from '../../../hooks/useAccount';
import useUserPreferences from '../../../hooks/useUserPreferences';
import { TrackingContext } from '../../../providers/tracking';
import { AgentContext } from '../../../types';
import {
  AI_MODELS,
  ANNOUNCEMENTS,
  DEFAULT_MODEL,
  SHOW_ANNOUNCEMENTS_ONLY_IN_EMPTY_STATE,
} from '../constants';
import AnnouncementsBar from './AnnouncementsBar';
import ContextChips from './ContextChips';
import EnvDisplayChip from './EnvDisplayChip';
import ModeSelector from './ModeSelector';
import ModelMenuSelector from './ModelMenuSelector';
import EmptyState from './EmptyState';
import SuggestionSection from './SuggestionSection';
import ChatMessage from './ChatMessage';
import QuotaBanner, { buildQuotaMessage } from './QuotaBanner';
import StuckMessage from './StuckMessage';
import AssistantStatusIndicator from './AssistantStatusIndicator';
// for debugging, allows us to:
// - see the tool invocations and results in the UI
// - control the model used for the chat
const isDebug = false;

const Wrapper = styled(FlexColumn)<{ $isEmpty?: boolean }>`
  height: 100%;
  justify-content: ${({ $isEmpty }) => ($isEmpty ? 'center' : 'space-between')};
  ${({ $isEmpty }) => $isEmpty && 'gap: 36px;'}
  position: relative;
`;

const FLOATING_FOOTER_CLEARANCE = 120;

const MessageSection = styled(FlexColumn)<{
  $noPadding?: boolean;
  $contentMaxWidth?: number;
  $bottomPadding: number;
}>`
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${({ $noPadding, $bottomPadding }) =>
    $noPadding ? 0 : `24px 24px ${$bottomPadding}px 24px`};
  ${({ $contentMaxWidth }) =>
    $contentMaxWidth && 'container-type: inline-size;'}
`;

const Footer = styled(Flex)<{
  $noPadding?: boolean;
  $maxWidth?: number;
  $floating?: boolean;
}>`
  padding: ${({ $noPadding }) => ($noPadding ? 0 : '0 24px 24px 24px')};
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : 'none')};
  width: 100%;
  align-self: center;
  ${({ $floating }) =>
    $floating &&
    `
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
  `}
`;

const borderGradient = css`
  border: 1px solid transparent !important;
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  background-image: linear-gradient(white, white),
    linear-gradient(to right, #f9c4ff, #fbc2eb, #a1c4fd);
`;

const askGradient = css`
  border: 1px solid transparent !important;
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  background-image: linear-gradient(white, white),
    linear-gradient(
      to right,
      ${({ theme }) => theme.palette.common.lightBlue4},
      ${({ theme }) => theme.palette.common.blue7}
    );
`;

const FooterContent = styled(FlexColumn)`
  width: 100%;
  position: relative;
`;

const AnnouncementsBarLayer = styled(Flex)`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  justify-content: center;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const InputContainer = styled(FlexColumn)`
  width: 100%;
  border-radius: 4px;
  ${borderGradient}
`;

const EnvRow = styled(Flex)`
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding: 8px 4px 0;
`;

const TextFieldWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const TextFieldStyled = styled(TextField)`
  .MuiInputBase-root {
    font-size: 14px;
  }

  .MuiInputBase-input {
    padding-right: 10px;
  }

  .MuiOutlinedInput-root {
    border: none !important;
    box-shadow: none !important;
    background: none !important;
    background-image: none !important;
    .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }
    :hover .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }
  }
`;

const SubmitArea = styled(Flex)`
  gap: 4px;
  align-items: center;
  justify-content: flex-end;
  padding: 4px 10px 10px;
`;

const SubmitButton = styled(IconButton)<{ $mode: ChatMode }>`
  ${({ $mode }) => ($mode === 'ask' ? askGradient : borderGradient)}

  :hover {
    background-image: linear-gradient(
        ${({ theme }) => theme.palette.common.gray8},
        ${({ theme }) => theme.palette.common.gray8}
      ),
      ${({ $mode, theme }) =>
        $mode === 'ask'
          ? `linear-gradient(to right, ${theme.palette.common.lightBlue4}, ${theme.palette.common.blue7})`
          : 'linear-gradient(to right, #f9c4ff, #fbc2eb, #a1c4fd)'} !important;
  }
`;

type Props = {
  chatEndpoint: string;
  placeholder?: string;
  startMessage?: string;
  noPadding?: boolean;
  title?: string;
  suggestions?: Suggestion[];
  chatHistory?: {
    threadId: string;
    messages: ChatMessageType[];
  };
  getToolRenderer?: (toolName?: string) => ToolRendererFunction | null;
  context?: AgentContext;
  onContextChange?: (context: AgentContext | undefined) => void;
  onEmptyStateChange?: (isEmpty: boolean) => void;
  onThreadIdChange?: (threadId: string) => void;
  contentMaxWidth?: number;
  availableModes?: ChatMode[];
  mode?: ChatMode;
  onModeChange?: (mode: ChatMode) => void;
  onBlockedAction?: (message: string) => void;
};

const AiChat = ({
  chatEndpoint,
  placeholder = 'Ask a question...',
  startMessage,
  noPadding,
  title = 'What are we working on today?',
  suggestions,
  chatHistory,
  getToolRenderer,
  context,
  onContextChange,
  onEmptyStateChange,
  onThreadIdChange,
  contentMaxWidth,
  availableModes = ['agent'],
  mode: selectedMode = 'agent',
  onModeChange: setSelectedMode,
  onBlockedAction,
}: Props) => {
  const queryClient = useQueryClient();
  const tracking = useContext(TrackingContext);
  const { pathname } = useLocation();
  const { data: sessionUsage, invalidateQuotaQuery } = useAiQuota();
  const monthlyLimitReached = !!sessionUsage?.monthly_limit_reached;
  const dailyLimitReached = !!sessionUsage?.exhausted;
  const quotaExhausted = monthlyLimitReached || dailyLimitReached;
  const quotaScope: QuotaScope = monthlyLimitReached ? 'monthly' : 'daily';
  const quotaResetsAt = monthlyLimitReached
    ? sessionUsage?.monthly_resets_at
    : sessionUsage?.resets_at;
  const quotaMessage = buildQuotaMessage(quotaScope, quotaResetsAt);
  const notifyBlocked = useCallback(() => {
    onBlockedAction?.(quotaMessage);
  }, [onBlockedAction, quotaMessage]);
  const { userPreferences, updateUserPreferences } = useUserPreferences();
  const selectedModel =
    AI_MODELS.find((m) => m.modelName === userPreferences.selectedAiModel) ??
    DEFAULT_MODEL;
  const setSelectedModel = useCallback(
    (model: ModelProvider) =>
      updateUserPreferences('selectedAiModel', model.modelName),
    [updateUserPreferences],
  );
  const [userScrolled, setUserScrolled] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [inputValue, setInputValue] = useState<string>('');
  const [startMessageSent, setStartMessageSent] = useState(false);
  const [shouldInvalidateHistory, setShouldInvalidateHistory] = useState(true);
  const [textIndent, setTextIndent] = useState(0);
  const [floatingFooterHeight, setFloatingFooterHeight] = useState(0);

  const bottomRef = useRef<HTMLDivElement>(null);
  const messageSectionRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chipOverlayRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const { data: account, isInitialLoading: isAccountLoading } = useAccount();
  const useNewInfra = !!account?.new_agents_infra;

  const legacyChat = useChatStream({
    api: chatEndpoint,
    selectedModel,
    chatHistory,
    context,
    mode: selectedMode,
    enabled: !useNewInfra,
  });
  const sessionChat = useSessionStream({
    chatHistory,
    context,
    mode: selectedMode,
    selectedModel,
    enabled: useNewInfra,
  });

  const {
    threadId,
    consolidatedMessages: messages,
    sendMessage,
    stop,
    isLoading,
    continueChat,
    approveToolUse,
    userInteraction,
    error,
  } = useNewInfra ? sessionChat : legacyChat;

  const isInitializing = useNewInfra ? sessionChat.isInitializing : false;

  useEffect(() => {
    onThreadIdChange?.(threadId);
  }, [threadId, onThreadIdChange]);

  const isEmpty = messages.length === 0;
  const showAnnouncements = SHOW_ANNOUNCEMENTS_ONLY_IN_EMPTY_STATE
    ? isEmpty
    : true;

  useEffect(() => {
    onEmptyStateChange?.(isEmpty);
  }, [isEmpty, onEmptyStateChange]);

  const prevIsLoading = usePrevious(isLoading);
  const prevMessages = usePrevious(messages);

  const chatMessageMaxWidth = useMemo(
    () => (contentMaxWidth ? contentMaxWidth - 60 : undefined),
    [contentMaxWidth],
  );

  const bottomPadding = useMemo(
    () =>
      contentMaxWidth
        ? Math.max(floatingFooterHeight + 24, FLOATING_FOOTER_CLEARANCE)
        : 0,
    [contentMaxWidth, floatingFooterHeight],
  );

  const isAssistantTyping = useMemo(() => {
    const lastMessage = messages[messages.length - 1];
    const lastMessagePart = lastMessage?.parts[lastMessage.parts.length - 1];
    if (isLoading && lastMessage) {
      const isUserMessage = lastMessage.role === 'user';

      const isToolResult =
        lastMessage.role === 'assistant' &&
        lastMessagePart?.type === 'tool-invocation' &&
        lastMessagePart.toolInvocation.state === 'result';

      return isUserMessage || isToolResult;
    }
    return false;
  }, [messages, isLoading]);

  const clearContextKeepEnvs = useCallback(() => {
    if (!context) return;
    const envIds = context.environmentIds;
    if (envIds && envIds.length > 0) {
      onContextChange?.({ environmentIds: envIds });
    } else {
      onContextChange?.(undefined);
    }
  }, [context, onContextChange]);

  const handleMessageSubmit = useCallback(() => {
    // Block sending until the account flag resolves; otherwise useNewInfra is
    // false mid-load and the message would be routed to the legacy backend.
    if (isAccountLoading || !inputValue.trim()) return;
    if (quotaExhausted) {
      notifyBlocked();
      return;
    }
    setShouldAutoScroll(true);
    setUserScrolled(false);
    tracking?.sendTrackingEvent({
      eventName: tracking.analytics.events.AI_CHAT_MESSAGE_SENT,
      properties: { source_page: pathname },
    });
    sendMessage(inputValue, context);
    setInputValue('');
    clearContextKeepEnvs();
  }, [
    inputValue,
    isAccountLoading,
    sendMessage,
    context,
    clearContextKeepEnvs,
    quotaExhausted,
    notifyBlocked,
    tracking,
    pathname,
  ]);

  useEffect(() => {
    if (prevIsLoading && !isLoading) {
      invalidateQuotaQuery();
    }
  }, [isLoading, prevIsLoading, invalidateQuotaQuery]);

  useEffect(() => {
    if (
      !isAccountLoading &&
      startMessage &&
      !isLoading &&
      !startMessageSent &&
      !quotaExhausted
    ) {
      sendMessage(startMessage, context);
      setStartMessageSent(true);
      clearContextKeepEnvs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMessage, isLoading, quotaExhausted, isAccountLoading]);

  // Scroll to bottom when messages change, but only if user hasn't scrolled
  // or if they've scrolled back to the bottom
  useEffect(() => {
    if (bottomRef.current && shouldAutoScroll && !userScrolled) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, shouldAutoScroll, userScrolled]);

  // Handle user scroll interaction
  useEffect(() => {
    const messageSection = messageSectionRef.current;
    if (!messageSection) return undefined;

    const handleScroll = () => {
      if (!messageSection) return;

      const isScrolledToBottom =
        messageSection.scrollHeight - messageSection.scrollTop <=
        messageSection.clientHeight + 50;

      // Only update userScrolled state if user has actually scrolled
      if (!isScrolledToBottom) {
        setUserScrolled(true);
        if (isLoading) {
          setShouldAutoScroll(false);
        }
      } else {
        setUserScrolled(false);
        setShouldAutoScroll(true);
      }
    };

    messageSection.addEventListener('scroll', handleScroll);
    return () => {
      messageSection.removeEventListener('scroll', handleScroll);
    };
  }, [isLoading]);

  // Track message updates during loading
  useEffect(() => {
    // When messages are updated during loading, maintain auto-scroll behavior
    // based on user's scroll position
    if (isLoading && prevMessages && messages.length !== prevMessages.length) {
      // Don't auto-scroll if user has manually scrolled up
      if (userScrolled) {
        setShouldAutoScroll(false);
      }
    }
  }, [isLoading, messages, prevMessages, userScrolled]);

  // Reset user scrolled state when sending a new message
  useEffect(() => {
    if (
      inputValue === '' &&
      messages.length > 0 &&
      messages[messages.length - 1].role === 'user'
    ) {
      setUserScrolled(false);
      setShouldAutoScroll(true);
    }
  }, [inputValue, messages]);

  useEffect(() => {
    if (prevIsLoading && !isLoading) {
      if (shouldInvalidateHistory) {
        setShouldInvalidateHistory(false);
        setTimeout(() => {
          queryClient.invalidateQueries([CHAT_HISTORY]);
        }, 2000);
      }
      inputRef.current?.focus();

      // If user hasn't manually scrolled, scroll to bottom when loading finishes
      if (!userScrolled) {
        setShouldAutoScroll(true);
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, prevIsLoading, userScrolled]);

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer || !contentMaxWidth) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setFloatingFooterHeight(entry.contentRect.height);
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, [contentMaxWidth]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea || !context) return undefined;

    const handleInputScroll = () => {
      if (chipOverlayRef.current) {
        chipOverlayRef.current.style.top = `${10 - textarea.scrollTop}px`;
      }
    };

    textarea.addEventListener('scroll', handleInputScroll);
    return () => textarea.removeEventListener('scroll', handleInputScroll);
  }, [context]);

  return (
    <Wrapper $isEmpty={isEmpty}>
      {!messages.length &&
        (isInitializing ? (
          <Flex sx={{ justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Flex>
        ) : (
          <EmptyState title={title} maxWidth={contentMaxWidth} />
        ))}

      {!!messages.length && (
        <MessageSection
          ref={messageSectionRef}
          $noPadding={noPadding}
          $contentMaxWidth={contentMaxWidth}
          $bottomPadding={bottomPadding}
        >
          {messages.map((message, i) => (
            <ChatMessage
              key={i}
              message={message}
              isDebug={isDebug}
              onApproveToolUse={approveToolUse}
              onUserInteraction={userInteraction}
              getToolRenderer={getToolRenderer}
              contentMaxWidth={chatMessageMaxWidth}
            />
          ))}

          {error && !isLoading && (
            <StuckMessage
              onContinue={quotaExhausted ? notifyBlocked : continueChat}
            />
          )}

          {(!isLoading || isAssistantTyping) && (
            <AssistantStatusIndicator
              status={isAssistantTyping ? 'working' : 'idle'}
              contentMaxWidth={chatMessageMaxWidth}
            />
          )}
          <div ref={bottomRef} />
        </MessageSection>
      )}

      <Footer
        ref={footerRef}
        $noPadding={noPadding}
        $maxWidth={
          contentMaxWidth && isEmpty
            ? Math.min(contentMaxWidth, 500)
            : contentMaxWidth
        }
        $floating={!!contentMaxWidth && !isEmpty}
      >
        <FooterContent>
          {quotaExhausted ? (
            <AnnouncementsBarLayer>
              <QuotaBanner scope={quotaScope} resetsAt={quotaResetsAt} />
            </AnnouncementsBarLayer>
          ) : (
            showAnnouncements && (
              <AnnouncementsBarLayer>
                <AnnouncementsBar announcements={ANNOUNCEMENTS} />
              </AnnouncementsBarLayer>
            )
          )}
          <InputContainer>
            <TextFieldWrapper>
              <ContextChips
                ref={chipOverlayRef}
                context={context}
                onWidthChange={setTextIndent}
                onClear={clearContextKeepEnvs}
              />
              <TextFieldStyled
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={placeholder}
                multiline
                maxRows={17}
                fullWidth
                autoFocus
                inputRef={inputRef}
                sx={
                  textIndent > 0
                    ? {
                        '& .MuiInputBase-input': {
                          textIndent: `${textIndent}px`,
                        },
                      }
                    : undefined
                }
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && !inputValue && context) {
                    clearContextKeepEnvs();
                  }
                  if (
                    e.key === 'Enter' &&
                    !e.shiftKey &&
                    !e.nativeEvent.isComposing &&
                    !isLoading
                  ) {
                    e.preventDefault();
                    handleMessageSubmit();
                  }
                  if (
                    e.key.toLowerCase() === 'c' &&
                    e.ctrlKey &&
                    isLoading &&
                    inputRef.current?.selectionStart ===
                      inputRef.current?.selectionEnd
                  ) {
                    stop();
                  }
                }}
              />
            </TextFieldWrapper>
            <SubmitArea>
              <Flex sx={{ marginRight: 'auto', alignItems: 'center', gap: 1 }}>
                {setSelectedMode && (
                  <ModeSelector
                    availableModes={availableModes}
                    selectedMode={selectedMode}
                    onChange={setSelectedMode}
                  />
                )}
                <ModelMenuSelector
                  selectedModel={selectedModel}
                  onChange={setSelectedModel}
                />
              </Flex>
              <SubmitButton
                $mode={selectedMode}
                disabled={
                  (isAccountLoading || !inputValue.trim()) && !isLoading
                }
                onClick={isLoading ? stop : handleMessageSubmit}
                variant="outlined"
              >
                {isLoading ? (
                  <StopRoundedIcon
                    sx={{
                      fontSize: '20px',
                      color:
                        selectedMode === 'ask'
                          ? 'common.blue7'
                          : 'common.magenta',
                    }}
                  />
                ) : (
                  <ArrowUpwardRoundedIcon
                    sx={{
                      fontSize: '20px',
                      color:
                        selectedMode === 'ask'
                          ? 'common.blue7'
                          : 'common.magenta',
                    }}
                  />
                )}
              </SubmitButton>
            </SubmitArea>
          </InputContainer>
          <EnvRow>
            <EnvDisplayChip
              context={context}
              onContextChange={onContextChange}
            />
          </EnvRow>
          {isEmpty && suggestions && suggestions.length > 0 && (
            <SuggestionSection
              suggestions={suggestions}
              onClick={(value) => {
                if (quotaExhausted) {
                  notifyBlocked();
                  return;
                }
                tracking?.sendTrackingEvent({
                  eventName: tracking.analytics.events.AI_CHAT_MESSAGE_SENT,
                  properties: { source_page: pathname },
                });
                sendMessage(value, context);
                clearContextKeepEnvs();
              }}
            />
          )}
        </FooterContent>
      </Footer>
    </Wrapper>
  );
};

export default AiChat;
